<template>
  <!-- Main Filters Container -->
  <aside
    v-click-outside="clickOutside"
    class="filters-wrapper d-lg-block"
    :class="{ open: showFilters, 'd-none': !isMobile }"
  >
    <!-- Overlay for mobile view -->
    <div v-show="showFilters" class="overlay" @click="closeFilters"></div>

    <!-- Sidebar Container -->
    <div class="sidebar-wrapper" :class="{ open: showFilters }" :style="styles">
      <div class="sidebar-fixed">
        <!-- Content Type Filters Section -->
        <div class="px-3">
          <div class="collapse-title">
            <!-- Mobile Close Button -->
            <div class="d-md-none d-flex justify-content-end w-100">
              <button
                class="btn"
                :aria-label="$t('main.searchResult.searchHeader.hideFiltersHeader')"
                @click="closeFilters"
              >
                <Icon icon="bootstrap-x" />
              </button>
            </div>
            <!-- Desktop Header -->
            <div class="align-items-center d-flex pt-md-2 w-100">
              <label v-b-toggle.collapse-1 for="Image type" class="title d-flex align-items-center">
                {{ $t('main.header.tags.content') }}
                <Icon icon="bootstrap-caret-up-fill" class="when-open" :width="9" :height="9" />
                <Icon icon="bootstrap-caret-down-fill" class="when-closed" :width="9" :height="9" />
              </label>
              <button
                class="btn d-none d-md-block px-0"
                :aria-label="$t('main.searchResult.searchHeader.hideFiltersHeader')"
                @click="closeFilters"
              >
                <Icon
                  icon="bootstrap-chevron-left"
                  class="vx-btn-chevron exp-bg-hover"
                  :width="12"
                  :height="12"
                />
              </button>
            </div>
          </div>
          <!-- Content Type Buttons -->
          <b-collapse id="collapse-1" visible>
            <UIButton
              v-for="(item, index) in orderedExperience"
              :key="index"
              :to="dimensionFilterPath(item)"
              v-bind="{
                ...commonButtonProps,
                ...bubbleOutlineProps(item.name)
              }"
              :enabled="hasResultsDimension(item.path)"
              :extra-data="{ event: '' }"
              @click.native="clickContentDimension(item)"
            >
              {{ item.name }}
            </UIButton>
          </b-collapse>
        </div>

        <hr v-if="attributesFilters" />

        <!-- Attributes Filters Section -->
        <div v-if="attributesFilters" class="px-3">
          <div class="collapse-title mt-2">
            <label
              v-b-toggle.collapse-filters
              for="Image type"
              class="title d-flex align-items-center"
            >
              {{ $t('main.searchResult.searchFilter.filters') }}
              <Icon icon="bootstrap-caret-up-fill" class="when-open" :width="9" :height="9" />
              <Icon icon="bootstrap-caret-down-fill" class="when-closed" :width="9" :height="9" />
            </label>
          </div>
          <!-- Attributes Toggle Buttons -->
          <b-collapse id="collapse-filters" visible>
            <b-form-group v-slot="{ ariaDescribedby }">
              <!-- Editable Text Filter -->
              <b-form-checkbox-group
                id="attributes-group"
                v-model="attributes"
                :aria-describedby="ariaDescribedby"
                switches
                stacked
              >
                <div class="d-flex py-1 filter-toggle">
                  <span class="mb-0">{{ $t('main.searchResult.searchFilter.editableText') }}</span>
                  <b-form-checkbox
                    class="ml-auto mr-n2 toggle"
                    value="editable_text"
                    @input="handleToggleAttribute($event, 'editable_text')"
                  />
                </div>
              </b-form-checkbox-group>

              <!-- Include T-shirt Maker Filter -->
              <b-form-checkbox-group
                id="attributes-group"
                v-model="attributes"
                :aria-describedby="ariaDescribedby"
                switches
                stacked
              >
                <div class="d-flex py-1 filter-toggle">
                  <span class="mb-0">{{ $t('main.searchResult.searchFilter.tShirtMaker') }}</span>
                  <b-form-checkbox
                    class="ml-auto mr-n2 toggle"
                    value="tshirt_maker"
                    @input="handleToggleAttribute($event, 'tshirt_maker')"
                  />
                </div>
              </b-form-checkbox-group>
              <!-- Exclude AI Filter -->
              <b-form-checkbox-group
                id="excluded-attributes-group"
                v-model="excludedAttributes"
                :aria-describedby="ariaDescribedby"
                switches
                stacked
              >
                <div class="d-flex py-1 filter-toggle">
                  <span class="mb-0">{{ $t('main.searchResult.searchFilter.excludeAi') }}</span>
                  <b-form-checkbox
                    class="ml-auto mr-n2 toggle"
                    value="ai"
                    @input="handleToggleAttribute($event, 'ai', true)"
                  />
                </div>
              </b-form-checkbox-group>
            </b-form-group>
          </b-collapse>
        </div>

        <hr />

        <!-- Niches Section -->
        <div class="px-3">
          <div v-if="nichesResult.length" class="collapse-title mt-2">
            <label
              v-b-toggle.collapse-niches
              for="Image type"
              class="title d-flex align-items-center"
            >
              {{ $t('main.header.tags.niches') }}
              <Icon icon="bootstrap-caret-up-fill" class="when-open" :width="9" :height="9" />
              <Icon icon="bootstrap-caret-down-fill" class="when-closed" :width="9" :height="9" />
            </label>
          </div>
          <!-- Niches Grid -->
          <b-collapse v-if="nichesResult.length" id="collapse-niches" visible class="row">
            <div v-for="item in nichesResult" :key="item.id" class="col-12 col-md-6 my-2">
              <nuxt-link class="exp-font" :to="$urlBuilder('niches', { slug: item.slug })">
                <button
                  type="button"
                  class="vx-btn small w-100 text-center d-block niche-block"
                  :style="`--bg-image: url('${item.thumbnail}')`"
                >
                  {{ item.name }}
                </button>
              </nuxt-link>
            </div>
          </b-collapse>
        </div>

        <hr />

        <!-- Related Tools Section -->
        <div class="px-3">
          <div class="collapse-title mt-2">
            <label v-b-toggle.collapse-3 for="Image type" class="title d-flex align-items-center">
              {{ $t('main.header.tags.alsoFromVexels') }}
              <Icon icon="bootstrap-caret-up-fill" class="when-open" :width="9" :height="9" />
              <Icon icon="bootstrap-caret-down-fill" class="when-closed" :width="9" :height="9" />
            </label>
          </div>
          <!-- Related Tools Links -->
          <b-collapse id="collapse-3" visible>
            <div class="content">
              <a
                href="https://www.vexels.com/t-shirt-maker/"
                rel="noopener noreferrer"
                target="_blank"
                >{{ $t('main.searchResult.searchFilter.tShirtMaker') }}</a
              >
              <a href="https://mockups.vexels.com/" rel="noopener noreferrer" target="_blank">{{
                $t('main.searchResult.searchFilter.mockupGenerator')
              }}</a>
              <a href="https://logomaker.vexels.com/" rel="noopener noreferrer" target="_blank">{{
                $t('main.searchResult.searchFilter.logoMaker')
              }}</a>
            </div>
          </b-collapse>
        </div>

        <hr />

        <!-- Mockups Promotion Section -->
        <div class="px-3">
          <nuxt-link :to="$urlBuilder('landing', { uid: 'mockups' })">
            <section class="card-see-more">
              <img
                class="img-mockup"
                loading="lazy"
                src="https://cdn.vexels.com/img/t-shirt-for-link-to-mockups-landing.png?fmt=webp"
                :alt="$t('main.searchResult.searchFilter.imageMockupAlt')"
              />
              <div class="content">
                <h5 class="title">
                  {{ $t('main.searchResult.searchFilter.findThePerfectMockup') }}
                </h5>
                <div class="see-more">
                  <p class="text-primary">{{ $t('main.searchResult.searchFilter.seeMore') }}</p>
                  <Icon :height="16" icon="bootstrap-arrow-right" />
                </div>
              </div>
            </section>
          </nuxt-link>
        </div>
      </div>
    </div>
  </aside>
</template>

<script>
import Icon from 'UI/Icon';
import { mapState, mapGetters } from 'vuex';
import UIButton from 'UI/Button';

export default {
  components: {
    Icon,
    UIButton
  },
  props: {
    attributesFilters: {
      type: Boolean,
      required: false,
      default: true
    }
  },
  data() {
    return {
      scrolled: 0,
      width: 0,
      top: 70,
      isMobile: false,
      attributes: [],
      excludedAttributes: []
    };
  },
  computed: {
    ...mapState('searchBar', ['actualSearch', 'hasSuggestionSelected', 'elementSelected']),
    ...mapState('search', ['showFilters']),
    ...mapGetters('search', ['nichesResult', 'getTotalResultsDimensions', 'getQuery']),
    ...mapGetters('dimensions', [
      'getFilterDimensionsByExperience',
      'getActualDimensionName',
      'getActualDimension'
    ]),
    orderedExperience() {
      return this.getFilterDimensionsByExperience('merch').sort((dim1, dim2) =>
        this.hasResultsDimension(dim1.path) === this.hasResultsDimension(dim2.path)
          ? 0
          : !this.hasResultsDimension(dim1.path)
          ? 1
          : -1
      );
    },
    commonButtonProps() {
      return {
        class: 'mb-2 mr-2 d-inline-flex',
        size: 'small-medium'
      };
    },
    styles() {
      return `top: ${this.top}px;`;
    },
    query() {
      return { ...this.$route.query, ...this.getQuery };
    }
  },
  mounted() {
    this.handleScroll();
    this.handleResize();
    window.addEventListener('scroll', this.handleScroll);
    window.addEventListener('resize', this.handleResize);
    this.top = document.getElementById('header-container').clientHeight;
    this.filterDisplayMobileCheck();
    window.addEventListener('resize', this.filterDisplayMobileCheck);
  },
  destroyed() {
    window.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('resize', this.handleResize);
    window.removeEventListener('resize', this.filterDisplayMobileCheck);
  },
  created() {
    const { attributes, excludedAttributes } = this.$route.query;
    this.attributes = attributes?.split(',') || [];
    this.excludedAttributes = excludedAttributes?.split(',') || [];
    this.setAttributes();
  },
  methods: {
    setAttributes() {
      const attributes = this.attributes.join(',');
      const excludedAttributes = this.excludedAttributes.join(',');
      let query = { ...this.query };
      if (attributes) query = { ...query, attributes };
      else delete query.attributes;
      if (excludedAttributes) query = { ...query, excludedAttributes };
      else delete query.excludedAttributes;
      this.$store.commit('search/SET_QUERY', query);
    },
    filterDisplayMobileCheck() {
      if (process.client) {
        if (window.innerWidth < 991) {
          this.$store.commit('search/SET_FILTERS_VISIBILITY', false);
          this.isMobile = true;
        }
      }
    },
    clickContentDimension(item) {
      if (this.hasResultsDimension(item.path)) {
        const url = this.dimensionFilterPath(item);
        this.$dataLayerPush('click_search_sidebar_content', {
          searchSidebarContent: {
            clickUrl: url,
            dimension: item.path
          }
        });
        this.$router.push(this.dimensionFilterPath(item));
      }
    },
    handleScroll() {
      this.scrolled = window.scrollY;
    },
    handleResize() {
      this.width = screen.width;
      this.top = document.getElementById('header-container').clientHeight;
    },
    closeFilters() {
      this.$store.commit('search/TOGGLE_FILTERS_VISIBILITY');
    },
    clickOutside(event) {
      if (
        !event.target.closest('#search-filter') &&
        this.filterDisplayMobileCheck() &&
        window.innerWidth <= 991
      )
        this.$store.commit('search/TOGGLE_FILTERS_VISIBILITY');
    },
    hasResultsDimension(dimPath) {
      return this.getTotalResultsDimensions[dimPath] !== 0;
    },
    dimensionFilterPath(item) {
      return !this.hasResultsDimension(item.path)
        ? ''
        : this.$urlBuilder('search', {
            path: item.path,
            keyword: this.actualSearch || '',
            query: this.query
          });
    },
    bubbleOutlineProps(name) {
      return this.getActualDimensionName == name
        ? {
            fontColor: 'white',
            shape: 'exp',
            text: 'white',
            style: "color: 'inherit'; --border: 1.5px solid transparent;"
          }
        : {
            color: '',
            fontColor: '#737373',
            shape: 'outline-hover',
            text: '',
            hover: 'exp',
            style: "color: 'inherit'; --border: 1.5px solid #E6E6E6;"
          };
    },
    async handleToggleAttribute(selected, item, excluded = false) {
      const label = excluded ? 'excludedAttributes' : 'attributes';
      const routeQuery = this.$route.query;
      const queryAttributes = routeQuery[label]?.split(',') || [];
      if (
        (queryAttributes.includes(item) && selected.includes(item)) ||
        (!queryAttributes.includes(item) && !selected.includes(item))
      ) {
        return;
      }
      const data = {
        path: this.getActualDimension.path,
        keyword: this.actualSearch || '',
        page: 1
      };
      const url = this.$urlBuilder('search', data);
      this.setAttributes();
      let attributes = this[label].join(',');
      let query = { ...routeQuery };
      if (attributes) query = { ...query, [label]: attributes };
      else delete query[label];
      await this.$router.replace({ path: url, query });
      const searchParams = {
        criteria: data.keyword,
        page: 1
      };
      this.$store.dispatch('search/MAKE_SEARCH', searchParams);
    }
  }
};
</script>

<style lang="scss" scoped>
#collapse-niches > div:nth-child(odd) {
  @include min-md {
    padding-right: 5px;
  }
}
#collapse-niches > div:nth-child(even) {
  @include min-md {
    padding-left: 5px;
  }
}
#collapse-niches > div button {
  color: #9d9d9d;
  &:hover {
    background-size: cover;
    background-position: center;
    color: #ffffff;
    background-image: linear-gradient(rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.3)), var(--bg-image);
  }
}
.image-section {
  text-align: center;
  width: 100%;
  height: 150px;
  margin: 10px 0px;
  display: flex;
  align-items: center;
  border-radius: 8px;
  color: #000000;
  background-size: cover;
  cursor: pointer;
  box-shadow: inset 0 0 0 2000px rgba(255, 255, 255, 0.5);
  filter: grayscale(1);
}
.image-section:hover {
  color: #ffffff;
  box-shadow: inset 0 0 0 2000px rgba(134, 134, 134, 0.4);
  filter: grayscale(0);
}
.image-section h5 {
  margin: auto;
  font-weight: 500;
}
.card-see-more {
  overflow: hidden;
  position: relative;
  --primary: #5287f2;
  --text: #737373;
  --time-transition: 0.3s;
  transition: background-size var(--time-transition);
  background: no-repeat top left/auto 103% #e6e6e6;
  border: 2px solid transparent;
  padding: 1.6rem 1.3rem 1rem;
  max-width: 17rem;
  border-radius: 19px;
  color: var(--text);
  &:hover {
    border: 2px solid var(--primary);
    .img-mockup {
      scale: 1.05;
      left: 15px;
    }
    .see-more div {
      transform: translateX(0px);
      opacity: 1;
    }
  }
  .title {
    position: relative;
    font-size: 19px;
    line-height: 18px;
    margin-bottom: 1rem;
    max-width: 6rem;
  }
  .see-more {
    position: relative;
    display: grid;
    grid-template-areas: 'common common-extended';
    align-items: center;
    gap: 0.3rem;
    width: max-content;
    color: var(--primary);
    p {
      margin: 0;
    }
    div {
      transform: translateX(-20px);
      opacity: 0;
      transition: transform var(--time-transition), opacity var(--time-transition);
      ::before {
        font-weight: bold;
      }
    }
  }
  .img-mockup {
    position: absolute;
    top: 0;
    left: 10px;
    object-fit: cover;
    width: 100%;
    height: 103%;
    transition: left 0.3s ease, scale 0.3s ease;
  }
}

.collapsed > .when-open,
.not-collapsed > .when-closed {
  display: none;
}
.title {
  font-weight: 500;
  font-size: 18px;
}
.filters-wrapper {
  position: relative;
  z-index: 0;
  opacity: 0;
  transition-property: opacity;
  transition-duration: 0.5s;
  @include lg {
    z-index: 500;
  }
}
.filters-wrapper.open {
  opacity: 1;
}
.collapse-title {
  flex-direction: column;
  justify-content: start;
  align-items: flex-start;
}
#collapse-3 {
  .content {
    display: grid;
    gap: 0.5rem;
  }
}

.overlay {
  display: none;
}
.sidebar-fixed {
  overflow: auto;
  height: calc(100vh - 80px);
  overflow-x: hidden;
}
.sidebar-wrapper {
  position: sticky;
  width: 305px;
  margin-left: -305px;
  transition: margin-left 0.5s;
  border-right: 1px solid #e6e6e6;
  background: #fff;
  z-index: 500;
  &.open {
    margin-left: 0px;
    .sidebar-fixed::-webkit-scrollbar {
      width: 5px;
    }
    .sidebar-fixed::-webkit-scrollbar-thumb {
      background: #e6e6e6;
      border-radius: 4px;
    }
    @include sm {
      width: 85vw;
    }
  }
  @include lg {
    position: fixed;
    top: 0 !important;
    width: 250px;
    height: 100%;
    margin-left: -250px;
  }
  @include sm {
    width: 100%;
    margin-left: -100%;
  }
}
.experience-container {
  .info {
    font-size: 14px;
    color: #535353;
    font-weight: 500;
  }
  .close-info {
    position: absolute;
    top: 0px;
    right: 6px;
  }
  position: relative;
  background: #f2f2f2;
  border: 1px solid #b8b8b8;
  border-radius: 6px;
  margin: 10px 0;
  padding: 20px 40px 20px 20px;
}
.experience-container:after,
.experience-container:before {
  bottom: 100%;
  border: solid transparent;
  content: '';
  height: 0;
  width: 0;
  position: absolute;
  left: 77px;
}
.experience-container:after {
  border-color: rgba(0, 0, 0, 0);
  border-bottom-color: #f2f2f2;
  border-width: 10px;
  margin-left: -10px;
}
.experience-container:before {
  border-color: rgba(161, 103, 45, 0);
  border-bottom-color: #8a8a8a;
  border-width: 11px;
  margin-left: -11px;
}

@media all and (max-width: 576px) {
  .sidebar-fixed {
    height: 100vh;
  }
  .overlay {
    display: block;
    opacity: 0.5;
    background: #000;
    width: 100%;
    height: calc(100vh + 80px);
    top: -80px;
    left: 0;
    position: fixed;
    z-index: 400;
  }
}
.vx-btn-chevron {
  border: var(--exp-color) solid 1px;
  border-radius: 100%;
  padding: 3px;
  color: var(--exp-color);
  transition: all 0.2s ease;
}
.vx-btn-chevron:hover {
  border: var(--exp-color) solid 1px;
  color: #fff;
  background-color: var(--exp-color);
}
.vx-btn.selected {
  color: white;
}
.filter-toggle {
  color: #737373;
}
</style>
